import { AccountSelector } from "@/components/AccountSelector";
import { CategorySelect } from "@/components/CategorySelect";
import { TransactionTag } from "@/components/TransactionTag";
import { Button } from "@groot/ui/button";
import { Calendar } from "@/app/shared/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/shared/ui/form";
import { Input } from "@groot/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/shared/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/shared/ui/sheet";
import { Switch } from "@/app/shared/ui/switch";
import { Textarea } from "@groot/ui/textarea";
import { useCreateTransaction } from "@/hooks/api";
import { cn } from "@/lib/utils";
import {
  type TransactionFormDataWithValidation,
  transactionFormSchema,
} from "@/schemas/transaction.schema";
import { type Account, type Category, type Tag, TransactionType } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { CalendarIcon, X } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

interface CreateTransactionSheetProps {
  accounts: Account[];
  categories: Category[];
  tags: Tag[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultAccountId?: number;
}

export function CreateTransactionSheet({
  accounts,
  categories,
  tags,
  isOpen,
  onOpenChange,
  defaultAccountId,
}: CreateTransactionSheetProps) {
  const createMutation = useCreateTransaction();

  const isLoading = createMutation.isPending;

  const form = useForm<TransactionFormDataWithValidation>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      date: dayjs().format("YYYY-MM-DD"),
      type: TransactionType.DEBIT,
      amount: 0,
      description: "",
      payee: "",
      merchantName: "",
      notes: "",
      referenceNo: "",
      accountId: defaultAccountId || accounts[0]?.id,
      categoryId: null,
      tagIds: [],
      isReviewed: false,
      isIncome: false,
      excludeFromBudget: false,
      paymentMode: undefined,
      currencyCode: "INR",
      tempDescription: "",
    },
  });

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (isOpen) {
      form.reset({
        date: dayjs().format("YYYY-MM-DD"),
        type: TransactionType.DEBIT,
        amount: 0,
        description: "",
        payee: "",
        merchantName: "",
        notes: "",
        referenceNo: "",
        accountId: defaultAccountId || accounts[0]?.id,
        categoryId: null,
        tagIds: [],
        isReviewed: false,
        isIncome: false,
        excludeFromBudget: false,
        paymentMode: undefined,
        currencyCode: "INR",
        tempDescription: "",
      });
    }
  }, [isOpen, defaultAccountId, accounts, form]);

  const onSubmit = async (data: TransactionFormDataWithValidation) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        onOpenChange(false);
        form.reset();
      },
    });
  };

  const selectedAccount = accounts.find((acc) => acc.id === form.watch("accountId"));

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(value) => {
        if (!value) {
          form.reset();
        }
        onOpenChange(value);
      }}
    >
      <SheetContent className="overflow-y-auto bg-background w-[400px] sm:w-[540px]">
        <Form {...form}>
          <SheetHeader>
            <SheetTitle>
              <div className="flex justify-between items-center">
                <span>Add Transaction</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            </SheetTitle>
            <SheetDescription asChild>
              <div className="text-sm text-muted-foreground">
                {selectedAccount ? (
                  <div className="flex items-center gap-2">
                    {selectedAccount.iconUrl && (
                      <img
                        src={selectedAccount.iconUrl}
                        alt={selectedAccount.accountName}
                        className="size-5 rounded object-cover"
                      />
                    )}
                    <span>
                      {selectedAccount.accountName} - {selectedAccount.lastFourAccountNumber}
                    </span>
                  </div>
                ) : (
                  "Select an account"
                )}
              </div>
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 border-b -mx-6" />

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Account Selection */}
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account</FormLabel>
                  <FormControl>
                    <AccountSelector
                      accounts={accounts}
                      value={field.value?.toString()}
                      onValueChange={(value) => field.onChange(Number(value))}
                      placeholder="Select account"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-5 gap-4">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col col-span-3">
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value ? (
                              dayjs(field.value).format("MMM DD, YYYY")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto size-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) =>
                            date && field.onChange(date.toISOString().split("T")[0])
                          }
                          disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem className="flex flex-col col-span-2">
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Amount"
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(Number.parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Add a description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <CategorySelect
                      categories={categories}
                      value={
                        typeof field.value === "number"
                          ? {
                              label: categories.find((c) => c.id === field.value)?.name || "",
                              value: field.value,
                            }
                          : null
                      }
                      onChange={(newValue) => {
                        field.onChange(newValue ? newValue.value : null);
                      }}
                      inputRef={field.ref}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select transaction type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={TransactionType.CREDIT}>Credit</SelectItem>
                        <SelectItem value={TransactionType.DEBIT}>Debit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="payee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payee</FormLabel>
                    <FormControl>
                      <Input placeholder="Payee name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="referenceNo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reference No.</FormLabel>
                    <FormControl>
                      <Input placeholder="Reference no." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tagIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <TransactionTag
                    options={tags}
                    transactionId={0}
                    inputRef={field.ref}
                    isCategoryLoading={false}
                    value={
                      field.value
                        ?.map((id) => tags.find((t) => t.id === id))
                        .filter(Boolean) as Tag[]
                    }
                    onChange={(newTags) => field.onChange(newTags?.map((t) => t.id) || [])}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add a note..."
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <FormField
                  control={form.control}
                  name="isReviewed"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">Reviewed</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="excludeFromBudget"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">Exclude from Budget</FormLabel>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" size="lg" disabled={isLoading}>
                  {isLoading ? "Creating..." : "Create Transaction"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
